import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import { MapPinOff } from "lucide-react";
import { api, fileUrl, formatTL } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/portfolioOptions";
import { BaseTiles, pinIcon, TURKEY_CENTER, ParcelShape } from "@/components/LocationPicker";
import { DirectionsButton } from "@/components/route/DirectionsDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const COLORS = { SALE: "#059669", RENT: "#2563EB", ARCHIVE: "#94a3b8" };
const FILTERS = [["ACTIVE", "Yayında"], ["SALE", "Satılık"], ["RENT", "Kiralık"], ["ARCHIVE", "Arşiv"], ["ALL", "Tümü"]];

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length) map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [40, 40], maxZoom: 15 });
  }, [points.map((p) => p.id).join()]);
  return null;
};

export default function MapPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("ACTIVE");

  useEffect(() => { api.get("/portfolios/map").then(({ data }) => setItems(data)); }, []);

  const shown = useMemo(() => items.filter((p) => {
    if (filter === "ALL") return true;
    if (filter === "ARCHIVE") return p.archived;
    if (filter === "ACTIVE") return !p.archived;
    return !p.archived && p.listing_type === filter;
  }), [items, filter]);

  return (
    <div className="space-y-6" data-testid="map-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Gold</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Portföy Haritası</h1>
          <p className="text-slate-500 mt-1">Ofisinizin konumu işaretlenmiş tüm portföyleri.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(([k, l]) => (
            <Button key={k} size="sm" data-testid={`map-filter-${k}`} variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)}>{l}</Button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs text-slate-600">
        <span data-testid="map-marker-count" className="font-semibold">{shown.length} konum</span>
        {[["SALE", "Satılık"], ["RENT", "Kiralık"], ["ARCHIVE", "Arşiv"]].map(([k, l]) => (
          <span key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: COLORS[k] }} />{l}</span>
        ))}
      </div>

      <Card className="overflow-hidden relative z-0">
        <div className="h-[calc(100vh-280px)] min-h-[420px]">
          <MapContainer center={TURKEY_CENTER} zoom={6} className="h-full w-full" zoomControl={false}>
            <ZoomControl position="topright" />
            <BaseTiles />
            <FitBounds points={shown} />
            {shown.filter((p) => p.parcel?.geometry).map((p) => <ParcelShape key={`g-${p.id}`} geometry={p.parcel.geometry} fit={false} />)}
            {shown.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(COLORS[p.archived ? "ARCHIVE" : p.listing_type])}>
                <Popup autoPanPadding={[60, 60]}>
                  <div className="w-52 space-y-1.5" data-testid={`map-popup-${p.id}`}>
                    {p.image_paths?.[0] && <img src={fileUrl(p.image_paths[0], "sm")} alt="" className="w-full h-24 object-cover rounded" />}
                    <div className="font-semibold text-slate-900">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.rooms} · {p.area_m2} m² · {p.district}, {p.city}</div>
                    <div className="font-bold text-emerald-700">{formatTL(p.price)}</div>
                    {p.archived && <div className="text-xs text-amber-700">{STATUS_LABEL[p.status]} (arşiv)</div>}
                    <DirectionsButton testid={`map-directions-${p.id}`} size="sm" className="w-full" target={{ lat: p.lat, lng: p.lng, geometry: p.parcel?.geometry, label: `${p.title} · ${p.district}, ${p.city}` }} />
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        {items.length === 0 && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/80">
            <div className="text-center text-slate-600">
              <MapPinOff className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              Konumu işaretlenmiş portföy yok.
              <div><Button variant="link" onClick={() => navigate("/portfolios")}>Portföye konum ekleyin</Button></div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
